import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.addEventListener('animationend', this.#freezeFinishedEntrance);
  }

  /**
   * Once an element's entrance animation has played, freeze it so it can't
   * replay while the drawer stays open. Some interactions display-toggle
   * drawer content (e.g. the region selector hides the search bar), and a
   * display toggle restarts CSS animations — without this, the search bar
   * re-faded-in every time the user came back from the region panel.
   * Cleared on full drawer close so the next open animates fresh.
   * @param {AnimationEvent} event
   */
  #freezeFinishedEntrance = (event) => {
    if (event.animationName !== 'menu-drawer-nav-open') return;
    const target = event.target;
    if (target instanceof HTMLElement && target.classList.contains('menu-drawer__animated-element')) {
      target.style.setProperty('animation', 'none');
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Mobile: category rows expand IN PLACE (accordion) instead of sliding
    // in a panel — the children push the rows below them down, and tapping
    // the row again folds them back (Jeff, 2026-09-07). Desktop keeps the
    // slide-in panel. The native <details> toggle is suppressed so the
    // height can animate (grid-template-rows 0fr -> 1fr in the CSS).
    if (details !== this.refs.details && this.#isAccordion(details)) {
      event?.preventDefault();
      this.#toggleAccordion(details);
      return;
    }

    // Update header group height
    const headerGroupHeight = calculateHeaderGroupHeight();
    document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);

    // If a scroll-jitter hide is in flight, restore the header so the
    // drawer opens under a visible, stably-positioned header. The drawer's
    // vertical position itself is pure CSS (keyed off --header-height /
    // --header-group-height per sticky state) — never measured in JS: a
    // JS measurement taken mid-animation bakes a transient offset into an
    // inline style, which is how the drawer used to open on top of the
    // header for its first 350ms.
    const headerComponent = document.querySelector('header-component');
    if (headerComponent && typeof headerComponent.pinRevealedHeader === 'function') {
      headerComponent.pinRevealedHeader();
    }

    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => details.classList.add('menu-open'));

    trapFocus(details);
  }

  /**
   * Whether a category <details> should behave as an inline accordion
   * (mobile viewport) rather than a slide-in panel.
   * @param {HTMLDetailsElement} details
   */
  #isAccordion(details) {
    return (
      details.classList.contains('menu-drawer__menu-container') &&
      window.matchMedia('(max-width: 749px)').matches
    );
  }

  /**
   * Expand or collapse one category in place. Only one category stays open
   * at a time so the list never grows past a thumb's reach.
   * @param {HTMLDetailsElement} details
   */
  #toggleAccordion(details) {
    if (details.classList.contains('is-expanded')) {
      this.#collapseAccordion(details);
      return;
    }

    this.querySelectorAll('details.menu-drawer__menu-container.is-expanded').forEach((other) => {
      if (other !== details && other instanceof HTMLDetailsElement) this.#collapseAccordion(other);
    });

    details.open = true;
    details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
    // Two frames: the content must be laid out at 0fr before the class
    // flips it to 1fr, or Safari skips the transition.
    requestAnimationFrame(() => requestAnimationFrame(() => details.classList.add('is-expanded')));
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  #collapseAccordion(details) {
    details.classList.remove('is-expanded');
    details.querySelector('summary')?.setAttribute('aria-expanded', 'false');

    const content = details.querySelector(':scope > .menu-drawer__submenu');
    const done = () => {
      if (!details.classList.contains('is-expanded')) details.removeAttribute('open');
    };
    if (!content || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      done();
      return;
    }

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      content.removeEventListener('transitionend', onEnd);
      done();
    };
    /** @param {TransitionEvent} e */
    const onEnd = (e) => {
      if (e.target === content && e.propertyName === 'grid-template-rows') finish();
    };
    content.addEventListener('transitionend', onEnd);
    setTimeout(finish, 450);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);

        // Unfreeze entrance animations so the next open replays them.
        details.querySelectorAll('.menu-drawer__animated-element').forEach((el) => {
          el.style.removeProperty('animation');
        });
      } else {
        trapFocus(this.refs.details);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.classList.remove('is-expanded');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
